import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { Button } from "@aidkitorg/component-library";
import { usePost } from "../API";
import InterfaceContext, { ConfigurationContext } from "../Context";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse, BUTTON_CLASS } from "../Util";
import { Attachment, createImageWindow, createPersistedVerificationKey, VerificationFieldContentType, AssistanceAnalysisContent } from "./Attachment";
import { QuestionProps } from "./Props";
import { getURLsFromCSV } from '@aidkitorg/roboscreener/lib/util/urls_from_csv'
import { useToast } from "@aidkitorg/component-library";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { ApplicantContext } from "../Contexts/ApplicantContext";
import { useAsyncEffect } from "../Hooks/AsyncEffect";
import { DocumentSignal, DocumentSignals, SignalResult } from "@aidkitorg/types/lib/survey";
import { SignalList } from "./SignalList";
import { hash } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import * as v0 from '@aidkitorg/types/lib/survey';
import { flattenAnalyses } from "@aidkitorg/types/lib/util";
import { useSurveySections } from "../Contexts/SurveySectionsContext";
import type { Sections } from "../Apply";

type RenderURLProps = {
  viewer?: 'screener' | 'applicant',
  url?: string,
  canRestore?: boolean,
  targetField: string,
  onRestore(url: string): void,
  signals?: SignalResult[],
  showFullUrls?: boolean,
  renderPublicImages?: boolean,
  versionMeta?: { created_at: string; author_name: string },
  assistance?: v0.Attachment['assistance'],
  uid?: string
};

function RenderURL({ viewer, url, canRestore, targetField, onRestore, signals, showFullUrls, versionMeta, renderPublicImages, assistance, uid }: RenderURLProps) {
  const { info } = useContext(ApplicantContext);
  const getVersions = usePost('/applicant/get_history');
  const [versionInfo, setVersionInfo] = useState<Record<string, any> | null>(null);

  const analysesList = useMemo(() => {
    if (!assistance) return [];
    return flattenAnalyses(assistance.analyses ?? [], info);
  }, [assistance, info]);

  useAsyncEffect(async (signal) => {
    if (!versionMeta || !assistance || !uid) {
      setVersionInfo(null);
      return;
    }

    const versionTimestamp = versionMeta.created_at;
    const historicalValues: Record<string, any> = {};

    for (const a of analysesList) {
      const fields = [a.targetField, ...(a.processAsync ? [`${a.targetField}_jobs`] : [])];
      for (const field of fields) {
        
        try {
          const fieldHistory = await getVersions({
            applicant: uid,
            key: field,
            end_date: versionTimestamp,
            limit: 1
          }, signal);


          if (fieldHistory.changes?.[0]) {
            historicalValues[field] = fieldHistory.changes[0].value;
          } else {
            historicalValues[field] = info[field];
          }
        } catch (error) {
          console.error(`Failed to fetch history for ${field}:`, error);
          historicalValues[field] = info[field];
        }
      }
    }

    setVersionInfo(historicalValues);
  }, [versionMeta, assistance, uid, getVersions, info, analysesList]);

  const infoToUse = useMemo(() => versionInfo ?? info, [versionInfo, info]);

  const { analysesForUrl, jobsForUrl } = useMemo(() => {
    if (!assistance || !url) {
      return { 
        analysesForUrl: {} as Record<string, string>, 
        jobsForUrl: {} as Record<string, { jobId: string, field?: string }> 
      };
    }

    const analyses: Record<string, string> = {};
    const jobs: Record<string, { jobId: string, field?: string }> = {};

    for (const a of analysesList) {
      const urlMap: Record<string, string> = safeParse(infoToUse[a.targetField!] ?? '{}', {});
      if (urlMap[url]) {
        analyses[a.targetField] = urlMap[url];
      }

      if (a.processAsync) {
        const urlJobMap: Record<string, { jobId: string, field?: string }> = safeParse(infoToUse[`${a.targetField}_jobs`!] ?? '{}', {});
        if (urlJobMap[url]) {
          jobs[a.targetField] = urlJobMap[url];
        }
      }
    }

    return { analysesForUrl: analyses, jobsForUrl: jobs };
  }, [assistance, analysesList, infoToUse, url]);

  if (!url) return <></>

  return <>
    {canRestore && <Button variant="secondary" onClick={() => onRestore(url)}>Restore</Button>}
    <Attachment key={url} info={infoToUse}
      targetField={targetField}
      url={url} Viewer={viewer} turnable={true} showFullUrls={showFullUrls} renderPublicImages={renderPublicImages} />
    {/* Display the timestamp and author above the signals if versionMeta is available. */}
    {versionMeta && (
      <span className="block text-xs text-gray-500 my-2">
        Set at {new Date(versionMeta.created_at).toLocaleString()} by {versionMeta.author_name?.replace('System', 'recipient')}
      </span>
    )}
    {assistance && (
      <AssistanceAnalysisContent
        analyses={analysesForUrl}
        jobs={jobsForUrl}
        assistance={assistance}
        info={infoToUse}
        uid={uid}
      />
    )}
    {typeof signals !== 'undefined' ? <SignalList signals={signals} /> : null}
  </>
}

function findAttachmentQuestion(sections: Sections | undefined, targetField: string) {
  for (const section of sections?.sections ?? []) {
    for (const q of section.Questions ?? []) {
      if (q["Field Type"] === "Attachment" && q["Target Field"] === targetField) {
        return q;
      }
    }
  }
  return undefined;
}

export function ShowField(props: QuestionProps) {
  const config = useContext(ConfigurationContext);
  const context = useContext(InterfaceContext);
  const getVersions = usePost('/applicant/get_history');
  const urls = getURLsFromCSV(props.info[props["Target Field"]!] || '');
  const [versions, setVersions] = useState(null as null | Awaited<ReturnType<typeof getVersions>>['changes']);
  const [versionsFetched, setVersionsFetched] = useState(false);
  const [showPreviousVersions, setShowPreviousVersions] = useState(false)
  const L = useLocalizedStrings();
  const content = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
  });
  const { toast } = useToast();

  const checks = usePost('/applicant/third_party_check_status');
  const [signals, setSignals] = useState<Record<string, SignalResult[]>>();

  const infoHash = useMemo(() => hash(JSON.stringify(props.info)), [props.info]);
  const { sections } = useSurveySections();
  
  const { showInlineAlerts } = safeParse(props.Metadata || '{}') as { showInlineAlerts?: boolean };
  
  // Only compute assistance if this field contains URLs (is an attachment field)
  const hasAttachmentUrls = props.info[props["Target Field"]!] && urls.some(u => u?.startsWith('http'));

  const assistance = useMemo(() => {
    if (!hasAttachmentUrls || !showInlineAlerts) return undefined;

    const targetField = props["Target Field"]!;
    const attachmentQuestion = findAttachmentQuestion(sections, targetField);

    if (!attachmentQuestion) return undefined;

    const config = safeParse(attachmentQuestion.Metadata || '{}') as v0.Attachment | undefined;
    return config?.assistance;
  }, [sections, props["Target Field"], hasAttachmentUrls, showInlineAlerts]);

  useAsyncEffect(async (cancel) => {
    if (props.Viewer === 'applicant') return;

    const verificationResultsField = createPersistedVerificationKey(props["Target Field"]!);
    const verificationResults: { [url: string]: VerificationFieldContentType } = safeParse(props.info![verificationResultsField], {});

    let signalAttachmentMap: Record<string, SignalResult[]> | undefined = undefined;
    if (verificationResults) {
      signalAttachmentMap = {};
      for (const url of urls) {
        let attachmentSignals: v0.SignalResult[] = [];
        const attachmentVerificationResults = verificationResults[url];
        if (attachmentVerificationResults) {

          if (attachmentVerificationResults.warnings?.length) {
            attachmentSignals.push({
              kind: 'Freeform Signal Result',
              found: true,
              config: {
                kind: 'Freeform Signal',
                description: attachmentVerificationResults.warnings.join('\n * '),
                instructions: '',
                name: 'Attachment Warnings',
                severity: 'warn'
              }
            });
          }
          if (attachmentVerificationResults.errors?.length) {
            attachmentSignals.push({
              kind: 'Freeform Signal Result',
              found: true,
              config: {
                kind: 'Freeform Signal',
                description: attachmentVerificationResults.errors.join('\n * '),
                instructions: '',
                name: 'Attachment Errors',
                severity: 'error'
              }
            });
          }

          if (attachmentVerificationResults.acceptabilityStatus && attachmentVerificationResults.acceptabilityStatus !== 'acceptable') {
            attachmentSignals.push({
              kind: 'Freeform Signal Result',
              found: true,
              config: {
                kind: 'Freeform Signal',
                description: `
                Status: ${attachmentVerificationResults.acceptabilityStatus}
                ${attachmentVerificationResults.acceptabilityMessage ?? ''}
              `,
                instructions: '',
                name: 'Attachment Acceptability',
                severity: 'info'
              }
            });
          }

          signalAttachmentMap[url] = attachmentSignals;
        }
      }
    }

    const signalResponse = await checks({
      uid: props.uid!,
      limitTo: 'Document Signals',
      includeTypeDetail: true
    }, cancel);

    if (signalResponse.length) {
      if (!signalAttachmentMap) {
        signalAttachmentMap = {};
      }
      for (const { targetField: signalTargetField, type } of signalResponse) {

        const config = type as v0.DocumentSignals
        if (!config.attachmentFields.includes(props["Target Field"]!)) {
          continue;
        }

        const results: Record<string, SignalResult[]> = safeParse(props.info![props["Target Field"]! + '_' + signalTargetField] ?? '{}');

        for (const [attachmentUrl, signalResults] of Object.entries(results)) {
          if (!signalAttachmentMap[attachmentUrl]) {
            signalAttachmentMap[attachmentUrl] = [];
          }

          signalAttachmentMap[attachmentUrl].push(...signalResults);
        }
      }
      setSignals(signalAttachmentMap);
    }
  }, [props.Viewer, infoHash, props["Target Field"]]);

  const decryptField = usePost("/admin/decrypt_field");

  // Automatically fetch versions on component mount for timestamp display
  useEffect(() => {
    if (props.uid && props["Target Field"] && !versions && !versionsFetched) {
      fetchVersions().catch(error => {
        console.error("Failed to fetch version history:", error);
      });
    }
  }, [props.uid, props["Target Field"], versions, versionsFetched]);

  if ((props["Additional Options"] || []).indexOf("Hidden") !== -1) {
    return <span></span>
  }

  async function fetchDecryptedValue() {
    if (!props.uid) {
      toast({
        description: "Cannot decrypt without an applicant",
        variant: 'error'
      });
      return;
    }

    const decrypted = await decryptField({
      applicant: props.uid,
      field: props["Target Field"]!
    });

    if (decrypted.decrypted) {
      alert(decrypted.decrypted);
    }
  }

  async function fetchVersions() {
    const history = await getVersions({
      applicant: props.uid!,
      key: props["Target Field"]!
    })
    setVersions(history.changes);
    setVersionsFetched(true);
  }

  function restoreURL(url?: string) {
    if (confirm("Are you sure you want to add this document to the current field?")) {
      const prev = props.info[props["Target Field"]!];
      if (prev) {
        props.setInfoKey(props["Target Field"]!, (prev + ',') + url || '', true, false);
      } else {
        props.setInfoKey(props["Target Field"]!, url || '', true, false);
      }
    }
  }

  const showVersions = (props["Additional Options"] || []).indexOf("Shows Versions") !== -1;
  const showFullUrls = (props["Additional Options"] || []).indexOf("Show Full Urls") !== -1;
  const renderPublicImages = (props["Additional Options"] || []).indexOf("Render Public Images") !== -1;

  if (
    props.info[props["Target Field"]!] &&
    urls.some(u => u?.startsWith('http'))
  ) {
    return (
      <div>
        {content}
        <ul>
          {urls.map((u, i) => {
            const versionMeta = versions?.find(v => v.key === props["Target Field"]);
            return <RenderURL
              key={i + '_' + u}
              signals={signals?.[u]}
              url={u}
              targetField={props["Target Field"]!}
              viewer={props.Viewer}
              onRestore={restoreURL}
              showFullUrls={showFullUrls}
              renderPublicImages={renderPublicImages}
              versionMeta={versionMeta}
              assistance={assistance}
              uid={props.uid}
            />
          })}
        </ul>
        {showVersions &&
          <div>
            {showPreviousVersions ?
              <div>
                <Button variant="secondary" onClick={() => setShowPreviousVersions(false)}>Hide Versions</Button>
                {(versions || []).map((v) => <div className="p-2 bg-gray-100 m-2 rounded-md">
                  {v.value ? getURLsFromCSV(v.value).map((u) => <RenderURL
                    targetField={props["Target Field"]!}
                    url={u}
                    signals={signals?.[u]}
                    canRestore={true}
                    viewer={props.Viewer}
                    onRestore={restoreURL}
                    showFullUrls={showFullUrls}
                    versionMeta={v}
                    assistance={assistance}
                    uid={props.uid}
                  />) : <div>(cleared)</div>}
                </div>)}
              </div>
              : <Button variant="secondary" onClick={
                () => setShowPreviousVersions(true)
              }>Show Versions</Button>
            }
          </div>}
      </div>
    );
  }

  // Styled show field
  const bg = 'light';
  let targetFieldValue = props.info[props["Target Field"]!];
  let isJSON = false;
  try {
    targetFieldValue = JSON.parse(targetFieldValue);
    isJSON = typeof targetFieldValue === 'object' && targetFieldValue !== null;
  } catch (e) { }

  return (
    <Card>
      <Card.Header as="h6">{props[languageContent(context.lang)]}</Card.Header>
      <Card.Body as="div" className={`alert alert-${bg} mb-0`}>
        <Card.Text className="text-gray-600 whitespace-pre-wrap">
          {props["Additional Options"]?.includes("Allow Decrypting") && config.roles?.includes('admin')
            ? <button className={BUTTON_CLASS} onClick={() => fetchDecryptedValue()}>Encrypted Value, Click to Decrypt</button>
            : isJSON
              ? <pre>{(JSON.stringify(targetFieldValue, null, 2)) + ''}</pre>
              : ![undefined, null, ''].includes(targetFieldValue)
                ? (targetFieldValue + '')
                : <em className="text-gray-600">{L.questions.empty}</em>
          }
          {showVersions &&
            <div>
              {showPreviousVersions ?
                <div className="mt-2">
                  <Button className="mb-2" variant="secondary" onClick={() => setShowPreviousVersions(false)}>Hide Versions</Button>
                  {(versions || []).map((v, vindex) => <div key={`${v}-${vindex}`} className="p-2 bg-gray-100 m-2 rounded-md">
                    <div>{v.value ? getURLsFromCSV(v.value).map((u) => <RenderURL
                      targetField={props["Target Field"]!}
                      url={u}
                      signals={signals?.[u]}
                      viewer={props.Viewer}
                      onRestore={restoreURL}
                      versionMeta={v}
                      assistance={assistance}
                      uid={props.uid}
                    />) : '(cleared)'}</div>
                  </div>)}
                </div>
                : <Button className="mt-2" variant="secondary" onClick={
                  () => setShowPreviousVersions(true)
                }>Show Versions</Button>
              }
            </div>}
        </Card.Text>
      </Card.Body>
    </Card>
  );
}

export function ShowDate(props: QuestionProps) {
  const context = useContext(InterfaceContext);

  const fetchDate = usePost('/applicant/get_history');

  const [date, setDate] = useState<Date | null>(null);

  useAsyncEffect(async (signal) => {
    if (!props["Target Field"] || !props.uid) return;

    const history = await fetchDate({
      applicant: props.uid!,
      key: props["Target Field"],
    }, signal);

    if (signal.aborted) return;

    const latest = history.changes.find((change) => change.key === props["Target Field"] && change.latest === true);
    if (latest) {
      setDate(new Date(latest.created_at));
    }
  }, [props["Target Field"], props.uid, fetchDate]);

  return (
    <Card>
      <Card.Header as="h6">{props[languageContent(context.lang)]}</Card.Header>
      <Card.Body as="div" className={`alert alert-light mb-0`}>
        <Card.Text className="text-gray-600">
          <div>
            <span>{date ? date.toLocaleString(context.lang.split('_')[0]) : ''}</span>
          </div>
        </Card.Text>
      </Card.Body>
    </Card>
  );
}

